import {
  MESSAGES_LEGACY_ENABLED_STATUSES,
  MESSAGES_LEGACY_READONLY_STATUSES,
  MESSAGES_POLICY_STATUSES,
  MESSAGES_RETENTION_WINDOW_MONTHS,
} from './messages.constants';

export type MessagePolicyState = 'HIDDEN' | 'INTERACTIVE' | 'READ_ONLY';

type LifecycleField = string | number | null;

export interface ComputeMessagePolicyInput {
  jobStatus?: string | null;
  purchaseOrderId?: LifecycleField;
  cancellationDate?: LifecycleField;
  translationCompleteDate?: LifecycleField;
  nowEpochMs?: number;
}

export interface MessagePolicyEvaluation {
  state: MessagePolicyState;
  anchorEpochMs: number | null;
  cutoffEpochMs: number | null;
  reason: string;
}

export function addCalendarMonths(epochMs: number, months: number): number {
  const date = new Date(epochMs);
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.getTime();
}

function normalizeStatus(status?: string | null): string {
  if (!status) return '';
  return status.toUpperCase().replaceAll(/[\s-]/g, '_');
}

function toEpochMs(value?: LifecycleField): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function hasPurchaseOrder(purchaseOrderId?: LifecycleField): boolean {
  if (purchaseOrderId === null || purchaseOrderId === undefined) return false;
  if (typeof purchaseOrderId === 'number') {
    return Number.isFinite(purchaseOrderId) && purchaseOrderId !== 0;
  }
  return purchaseOrderId.trim().length > 0;
}

function evaluateAnchoredRetention(args: {
  anchorValue?: LifecycleField;
  nowEpochMs: number;
  missingReason: string;
  inWindowReason: string;
  expiredReason: string;
}): MessagePolicyEvaluation {
  const anchorEpochMs = toEpochMs(args.anchorValue);
  if (!anchorEpochMs) {
    return readonlyWithReason(args.missingReason);
  }

  const cutoffEpochMs = addCalendarMonths(
    anchorEpochMs,
    MESSAGES_RETENTION_WINDOW_MONTHS,
  );

  const isInteractive = args.nowEpochMs < cutoffEpochMs;
  return {
    state: isInteractive ? 'INTERACTIVE' : 'READ_ONLY',
    anchorEpochMs,
    cutoffEpochMs,
    reason: isInteractive ? args.inWindowReason : args.expiredReason,
  };
}

function readonlyWithReason(reason: string, anchorEpochMs: number | null = null): MessagePolicyEvaluation {
  // Keep degraded behavior explicit for support diagnostics while preserving read access.
  console.warn(`[messages-policy] ${reason}`);
  return {
    state: 'READ_ONLY',
    anchorEpochMs,
    cutoffEpochMs: anchorEpochMs
      ? addCalendarMonths(anchorEpochMs, MESSAGES_RETENTION_WINDOW_MONTHS)
      : null,
    reason,
  };
}

export function computeMessagePolicy(
  input: ComputeMessagePolicyInput,
): MessagePolicyEvaluation {
  const status = normalizeStatus(input.jobStatus);
  const nowEpochMs = input.nowEpochMs ?? Date.now();

  if (!status) {
    return {
      state: 'HIDDEN',
      anchorEpochMs: null,
      cutoffEpochMs: null,
      reason: 'Missing job status',
    };
  }

  if (status === MESSAGES_POLICY_STATUSES.inProgress) {
    return {
      state: 'INTERACTIVE',
      anchorEpochMs: null,
      cutoffEpochMs: null,
      reason: 'In progress jobs are interactive',
    };
  }

  if (status === MESSAGES_POLICY_STATUSES.cancelled) {
    if (!hasPurchaseOrder(input.purchaseOrderId)) {
      return {
        state: 'HIDDEN',
        anchorEpochMs: null,
        cutoffEpochMs: null,
        reason: 'Cancelled without purchase order',
      };
    }

    return evaluateAnchoredRetention({
      anchorValue: input.cancellationDate,
      nowEpochMs,
      missingReason:
        'Cancelled with purchase order but missing or invalid cancellationDate',
      inWindowReason: 'Cancelled within retention window',
      expiredReason: 'Cancelled retention window expired',
    });
  }

  if (status === MESSAGES_POLICY_STATUSES.completed) {
    // Completion in this workflow implies the job reached active processing.
    return evaluateAnchoredRetention({
      anchorValue: input.translationCompleteDate,
      nowEpochMs,
      missingReason: 'Completed with missing or invalid translationCompleteDate',
      inWindowReason: 'Completed within retention window',
      expiredReason: 'Completed retention window expired',
    });
  }

  if (
    (MESSAGES_LEGACY_ENABLED_STATUSES as readonly string[]).includes(status)
  ) {
    return {
      state: (MESSAGES_LEGACY_READONLY_STATUSES as readonly string[]).includes(
        status,
      )
        ? 'READ_ONLY'
        : 'INTERACTIVE',
      anchorEpochMs: null,
      cutoffEpochMs: null,
      reason: 'Legacy message visibility behavior',
    };
  }

  return {
    state: 'HIDDEN',
    anchorEpochMs: null,
    cutoffEpochMs: null,
    reason: 'Status does not allow messages',
  };
}
